import React, { useState } from 'react';
import { exportCSV, volumeClass, rankClass } from '../utils/export';
import { STAGES } from '../hooks/useLookup';

/* ── ProductCard ─────────────────────────────────────────────── */
export function ProductCard({ product }) {
  return (
    <div className="product-card">
      {product.image ? (
        <img
          className="product-img"
          src={product.image}
          alt={product.title}
          onError={e => { e.target.style.display = 'none'; }}
        />
      ) : (
        <div className="product-img-placeholder">📦</div>
      )}
      <div className="product-info">
        <div className="product-title">{product.title}</div>
        <div className="product-meta">
          <span className="asin-chip">{product.asin}</span>
          {product.brand && <span className="meta-chip">🏷 {product.brand}</span>}
          {product.rating && <span className="meta-chip">⭐ {product.rating}</span>}
          {product.category?.[0] && <span className="meta-chip">📂 {product.category[0]}</span>}
        </div>
      </div>
    </div>
  );
}

/* ── ProgressView ────────────────────────────────────────────── */
const STEP_LABELS = {
  [STAGES.SCRAPING]: 'Fetch product',
  [STAGES.EXTRACTING]: 'Extract seeds',
  [STAGES.EXPANDING]: 'Autocomplete',
  [STAGES.RANKING]: 'Check ranks',
  [STAGES.COMPLETE]: 'Done',
};

const STAGE_ORDER = [STAGES.SCRAPING, STAGES.EXTRACTING, STAGES.EXPANDING, STAGES.RANKING, STAGES.COMPLETE];

export function ProgressView({ stage, message, progressPct, liveKeywords }) {
  const stageIdx = STAGE_ORDER.indexOf(stage);

  return (
    <div className="progress-card">
      <div className="progress-icon">⌛</div>
      <div className="progress-title">Working on it…</div>
      <div className="progress-message">{message}</div>

      <div className="progress-bar-wrap">
        <div className="progress-bar-fill" style={{ width: `${progressPct}%` }} />
      </div>

      <div className="progress-steps">
        {STAGE_ORDER.filter(s => s !== STAGES.COMPLETE).map((s, i) => (
          <div
            key={s}
            className={`step-item ${i === stageIdx ? 'active' : ''} ${i < stageIdx ? 'done' : ''}`}
          >
            <span className="step-dot" aria-hidden="true"></span>
            {STEP_LABELS[s]}
          </div>
        ))}
      </div>

      {liveKeywords.length > 0 && (
        <div style={{
          marginTop: 20,
          padding: '12px 16px',
          background: 'var(--bg-subtle)',
          borderRadius: 'var(--radius-md)',
          textAlign: 'left'
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
            Live findings
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {liveKeywords.slice(0, 12).map((kw, i) => (
              <span key={i} style={{
                fontSize: 12,
                background: 'var(--success-soft)',
                color: 'var(--success)',
                padding: '2px 8px',
                borderRadius: 100,
                fontWeight: 500,
                animation: 'fadeIn 0.3s ease',
              }}>
                #{kw.rank} {kw.keyword}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── ResultsTable ────────────────────────────────────────────── */
const FILTERS = [
  { label: 'All keywords', key: 'all' },
  { label: 'Page 1', key: 'p1' },
  { label: 'Top 10', key: 'top10' },
  { label: 'Organic only', key: 'organic' },
];

export function ResultsTable({ results, asin }) {
  const [filter, setFilter] = useState('all');

  const filtered = results.filter(r => {
    if (filter === 'p1') return r.page === 1;
    if (filter === 'top10') return r.rank <= 10;
    if (filter === 'organic') return !r.sponsored;
    return true;
  });

  return (
    <div>
      <div className="results-header">
        <h2 className="results-title">
          Ranking keywords
        </h2>
        <div className="results-meta">
          <span className="result-stat">✓ {results.length} found</span>
          <button
            className="export-btn"
            onClick={() => exportCSV(results, asin)}
            aria-label="Export as CSV"
          >
            ↓ Export CSV
          </button>
        </div>
      </div>

      <div className="filter-bar">
        {FILTERS.map(f => (
          <button
            key={f.key}
            className={`filter-chip ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="results-table" role="table" aria-label="Keyword rankings">
        <div className="table-head" role="row">
          <span>Rank</span>
          <span>Keyword</span>
          <span>Volume</span>
          <span>Page</span>
          <span>Type</span>
        </div>
        {filtered.length === 0 && (
          <div style={{ padding: '28px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
            No keywords match this filter.
          </div>
        )}
        {filtered.map((r, i) => (
          <div key={i} className="result-row" role="row">
            <div className={`rank-badge ${rankClass(r.rank)}`} aria-label={`Rank ${r.rank}`}>
              {r.rank}
            </div>
            <div className="keyword-text" title={r.keyword}>{r.keyword}</div>
            <div>
              <span className={`vol-badge ${volumeClass(r.volume)}`}>
                {r.volume || '—'}
              </span>
            </div>
            <div className="page-indicator">pg {r.page}</div>
            <div>
              {r.sponsored
                ? <span className="sponsored-tag">Sponsored</span>
                : <span style={{ fontSize: 12, color: 'var(--success)', fontWeight: 500 }}>Organic</span>
              }
            </div>
          </div>
        ))}
      </div>

      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12, textAlign: 'center' }}>
        Volume estimates are heuristic-based. Rank positions reflect live Amazon data at time of lookup.
      </p>
    </div>
  );
}

/* ── EmptyState / How it works ───────────────────────────────── */
export function EmptyState() {
  const steps = [
    {
      num: '1',
      title: 'Find a competitor ASIN',
      desc: 'Open any Amazon product you want to spy on. Copy the ASIN from the URL or product details.',
    },
    {
      num: '2',
      title: 'We dig the rankings',
      desc: 'Our tool scrapes Amazon directly, extracting every keyword this product organically ranks for.',
    },
    {
      num: '3',
      title: 'You take the advantage',
      desc: 'Use those keywords in your listings, ads, and content. Close the gap or widen your lead.',
    },
  ];

  return (
    <div className="how-it-works">
      <p className="section-label">How it works</p>
      <div className="steps-grid">
        {steps.map(s => (
          <div key={s.num} className="step-card">
            <div className="step-num">{s.num}</div>
            <div className="step-title">{s.title}</div>
            <p className="step-desc">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── ErrorCard ───────────────────────────────────────────────── */
export function ErrorCard({ message }) {
  return (
    <div className="error-card">
      <div className="error-icon">⚠</div>
      <div className="error-title">Something didn't go as planned</div>
      <p className="error-msg">
        {message || 'An unexpected error occurred.'}
        {' '}
        If this keeps happening, take a screenshot and reach out through the contact section below — I'll look into it personally.
      </p>
    </div>
  );
}
